import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import ErrorBoundary from '../ErrorBoundary';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { LayoutDashboard, FileText, DollarSign, ClipboardList, PhoneCall, Crown, Wrench, LogOut, User, ChevronDown } from 'lucide-react';
import Logo from '../Logo';
import NotificationBell from '../NotificationBell';
import AnnouncementsBanner from '../AnnouncementsBanner';
import ImpersonationBanner from '../ImpersonationBanner';
import PreviewBanner from '../PreviewBanner';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu';

const CustomerLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/customer/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/customer/invoices', label: 'Invoices', icon: FileText },
    { path: '/customer/spending', label: 'Spending', icon: DollarSign },
    { path: '/customer/estimates', label: 'Estimates', icon: ClipboardList },
    { path: '/customer/service-request', label: 'Service Request', icon: PhoneCall },
    { path: '/customer/membership', label: 'Membership', icon: Crown },
    { path: '/customer/equipment', label: 'My Equipment', icon: Wrench },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] pl-safe pr-safe">
      <header className="bg-[#141414] border-b border-[#262626] sticky top-0 z-50 pt-safe-header">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Logo size="md" showTagline={false} />
              <div className="hidden md:block border-l border-[#262626] pl-4">
                <p className="text-[#A3A3A3] text-xs uppercase tracking-[0.2em]">Customer Portal</p>
                <p className="text-[#D4AF37] text-xs font-bold tracking-wider mt-1">LOCAL. TRUSTED. RELIABLE.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="profile-menu-trigger"
                    className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-md hover:bg-[#1a1a1a] transition-colors"
                  >
                    <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-[#0A0A0A] flex items-center justify-center text-xs font-bold">
                      {(user?.name || '?').slice(0, 1).toUpperCase()}
                    </span>
                    <span className="hidden sm:inline text-sm text-white max-w-[140px] truncate">{user?.name}</span>
                    <ChevronDown className="hidden sm:inline w-3 h-3 text-[#A3A3A3]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#141414] border-[#262626] text-white w-52">
                  <DropdownMenuLabel className="text-[#A3A3A3]">
                    <p className="text-white truncate">{user?.name}</p>
                    <p className="text-[10px] uppercase tracking-wider text-[#525252] mt-0.5">{user?.role}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[#262626]" />
                  <DropdownMenuItem
                    data-testid="profile-menu-item"
                    onClick={() => navigate('/customer/profile')}
                    className="cursor-pointer text-sm text-white hover:!bg-[#D4AF37]/15 hover:!text-[#D4AF37]"
                  >
                    <User className="w-4 h-4 mr-2" /> My Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#262626]" />
                  <DropdownMenuItem
                    data-testid="logout-menu-item"
                    onClick={handleLogout}
                    className="cursor-pointer text-sm text-white hover:!bg-[#7F1D1D]/30 hover:!text-red-300"
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <nav className="bg-[#141414] border-b border-[#262626] sticky top-[60px] sm:top-[72px] z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex gap-1 overflow-x-auto no-scrollbar">
            {navItems.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all duration-200 whitespace-nowrap ${
                  location.pathname === path
                    ? 'border-[#D4AF37] text-[#D4AF37]'
                    : 'border-transparent text-[#A3A3A3] hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8 pb-safe">
        <AnnouncementsBanner />
        <ImpersonationBanner />
        <PreviewBanner />
        <ErrorBoundary scope={`customer:${location.pathname}`} key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>

      <footer className="border-t border-[#262626] mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase mb-2">Our Promise to You</p>
            <p className="text-white text-sm">We show up on time. We do the job right. We treat your home like it's our own.</p>
          </div>
          <div>
            <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase mb-2">Contact</p>
            <a href="tel:6142854502" className="text-white text-sm block hover:text-[#D4AF37]">614-285-4502</a>
            <a href="mailto:team@nexflowplumbing.com" className="text-white text-sm block hover:text-[#D4AF37]">team@nexflowplumbing.com</a>
            <a href="https://www.nexflowplumbing.com" target="_blank" rel="noopener noreferrer" className="text-white text-sm block hover:text-[#D4AF37]">www.nexflowplumbing.com</a>
          </div>
          <div>
            <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase mb-2">Local. Trusted. Reliable.</p>
            <p className="text-[#A3A3A3] text-sm">© Nexflow Inc. — Premium Plumbing & Drain Cleaning</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CustomerLayout;